import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getReservations } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, LogOut } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solaro-founding/artifacts/6pe0ovti_Solaro%20Capri%20Gin%20-%20Horizontal%20Vectorial%20Handpainted%20PNG%20TRANSPARENT.png";

export default function ReservationsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getReservations();
        setData(res.data);
      } catch {
        toast.error("Failed to load reservations");
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const confirmed = data?.bottles?.filter((b) => b.status === "confirmed") || [];
  const held = data?.bottles?.filter((b) => b.status === "held") || [];

  return (
    <div className="min-h-screen" data-testid="reservations-page">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-[#D1CFC7] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Solaro" className="h-8 sm:h-10 object-contain" />
          <div className="flex items-center gap-3">
            <button
              data-testid="back-to-grid-link"
              onClick={() => navigate("/grid")}
              className="text-xs uppercase tracking-widest text-[#002366] hover:text-[#D4AF37] font-body transition-colors hidden sm:flex items-center gap-1"
            >
              <ArrowLeft size={14} /> Grid
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="p-2 text-[#1A2B4C]/40 hover:text-[#EA580C] transition-colors"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-12">
        <div className="animate-fade-in-up">
          <h1 className="font-display text-2xl sm:text-4xl text-[#002366] mb-2 tracking-tight">
            My Reservations
          </h1>
          <p className="font-body text-sm text-[#1A2B4C]/50 mb-8">
            Welcome, {user?.name}. Here are your bottles.
          </p>

          {/* Confirmed bottles */}
          {confirmed.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xs uppercase tracking-widest text-emerald-600 font-body mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                Confirmed Bottles
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {confirmed.map((b) => (
                  <div key={b.number} className="bg-white p-6 shadow-sm text-center">
                    <div className="w-16 h-24 bg-[#002366] rounded-t-full rounded-b-md flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <span className="font-display text-xl text-white font-bold">{b.number}</span>
                    </div>
                    <p className="font-display text-sm text-[#002366]">Bottle #{b.number}</p>
                    <Badge className="bg-emerald-100 text-emerald-700 border-none text-xs mt-2">Confirmed</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Held bottles */}
          {held.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xs uppercase tracking-widest text-[#D4AF37] font-body mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                Pending Deposit
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {held.map((b) => (
                  <div key={b.number} className="bg-white p-6 shadow-sm text-center">
                    <div className="w-16 h-24 bg-[#D4AF37] rounded-t-full rounded-b-md flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <span className="font-display text-xl text-white font-bold">{b.number}</span>
                    </div>
                    <p className="font-display text-sm text-[#002366]">Bottle #{b.number}</p>
                    <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-none text-xs mt-2">Held</Badge>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Button
                  data-testid="complete-deposit-btn"
                  onClick={() => navigate("/grid")}
                  className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-5"
                >
                  Complete Deposit
                </Button>
              </div>
            </div>
          )}

          {confirmed.length === 0 && held.length === 0 && (
            <div className="text-center py-16 bg-white shadow-sm">
              <p className="font-body text-[#1A2B4C]/40 mb-4">No bottles reserved yet.</p>
              <Button
                data-testid="go-to-grid-btn"
                onClick={() => navigate("/grid")}
                className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-5"
              >
                Browse the Grid
              </Button>
            </div>
          )}

          {/* What's included */}
          <div className="mt-10 p-6 sm:p-8 bg-white shadow-sm">
            <h3 className="font-display text-xl text-[#002366] mb-4">What Your Bottle Includes</h3>
            <ul className="space-y-3 font-body text-sm text-[#1A2B4C]/70 leading-relaxed">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                <span><strong>Il Versatore</strong> — the Solaro glass pourer, part of the serving ritual</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                <span><strong>A signed postcard</strong> from Lorenzo</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                <span><strong>Founding member status</strong> in the Capri Polo Club (details at launch)</span>
              </li>
            </ul>
          </div>

          {/* Lorenzo's note */}
          <div className="mt-6 p-6 sm:p-8 bg-[#002366]/5 border border-[#002366]/10">
            <p className="font-body text-sm text-[#002366] italic leading-relaxed">
              "Thank you for being part of the founding 300. Every bottle carries a story — and now, one carries your name. See you on the island."
            </p>
            <p className="font-body text-xs text-[#002366]/60 mt-3">— Lorenzo, Founder</p>
          </div>
        </div>
      </div>
    </div>
  );
}
